/**
 * Game mode picker used in the TV lobby sidebar.
 */

import { useTranslation } from 'react-i18next'
import { EmojiText } from '@/components/low/AnimatedEmoji'
import type { GameModeListItem } from '@/services/api'

interface GameModeSelectorProps {
  gameModes: GameModeListItem[]
  selectedModeId: string
  onSelect: (modeId: string) => void
}

export function GameModeSelector({ gameModes, selectedModeId, onSelect }: GameModeSelectorProps) {
  const { t } = useTranslation()

  return (
    <div className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-4 flex flex-col min-h-0">
      <p className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground font-black mb-3 flex-shrink-0">
        {t('lobby.gameMode')}
      </p>
      <div className="flex flex-col gap-2 min-h-0 overflow-y-auto custom-scrollbar">
        {gameModes.map((mode) => (
          <button
            key={mode.id}
            type="button"
            aria-pressed={selectedModeId === mode.id}
            onClick={() => onSelect(mode.id)}
            className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-all text-left border ${
              selectedModeId === mode.id
                ? 'bg-primary/20 border-primary text-primary'
                : 'border-border text-muted-foreground hover:bg-muted/50 hover:text-foreground'
            }`}
          >
            <span className="text-xl"><EmojiText>{mode.icon}</EmojiText></span>
            <div>
              <p className="text-xs font-bold tracking-wide">{mode.title}</p>
              <p className="text-[10px] opacity-60">{t('lobby.playerCount', { count: mode.players })}</p>
            </div>
            {selectedModeId === mode.id && (
              <span className="ml-auto text-[10px] font-black uppercase tracking-widest">✔</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
